import { Fragment, Suspense, lazy, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'sonner';
import { 
  Crown, LayoutDashboard, Users, Gamepad2, Settings, LogOut, 
  DollarSign, MessageCircle, ChevronRight, Search, Bell, Menu,
  Building2, Shield, ChevronDown, BarChart3, FileText, Server, Trophy, Bot, Star, Activity, Zap, PieChart, Palette, Layers, Gift,
  Wallet, Megaphone, Tag, Download, Mail, FlaskConical, Phone, Sparkles, Cloud, CreditCard, Bug, ScrollText, Disc3, ShoppingBag, CalendarDays, Plug, ClipboardList, Lock, ShieldCheck
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  hasAdminWorkspaceRole,
  hasLiveChatMenu,
  OPEN_ADMIN_WORKSPACE_MENU_EVENT,
} from './adminWorkspacePolicy';

const ADMIN_PAGE_LOADERS = {
  dashboard: () => import('./DashboardPage'),
  players: () => import('./PlayersPage'),
  transactions: () => import('./TransactionsPage'),
  'game-operations': () => import('./GameOperationsPage'),
  games: () => import('./GamesPage'),
  providers: () => import('./ProvidersPage'),
  payments: () => import('./PaymentMethodsPage'),
  tenants: () => import('./TenantsPage'),
  staff: () => import('./StaffPage'),
  chat: () => import('./ChatSupportPage'),
  'game-records': () => import('./GameRecordsPage'),
  settings: () => import('./SettingsPage'),
  banners: () => import('./BannersPage'),
  'tenant-analytics': () => import('./TenantAnalyticsPage'),
  'tasks-rewards': () => import('./TasksRewardsPage'),
  telegram: () => import('./TelegramConfigPage'),
  notifications: () => import('./NotificationsPage'),
  'deposit-requests': () => import('./DepositRequestsPage'),
  'withdrawal-requests': () => import('./WithdrawalRequestsPage'),
  'withdrawal-portal': () => import('./WithdrawalPortalPage'),
  tiers: () => import('./TiersPage'),
  'provider-health': () => import('./ProviderHealthPage'),
  'tenant-dashboard': () => import('./TenantDashboardPage'),
  'tenant-settings': () => import('./TenantSettingsPage'),
  branding: () => import('./TenantBrandingPage'),
  activity: () => import('./ActivityAnalyticsPage'),
  'user-analytics': () => import('./UserAnalyticsPage'),
  'player-analytics': () => import('./PlayerAnalyticsPage'),
  'operations-center': () => import('./OperationsCenterPage'),
  'staff-activity': () => import('./StaffActivityPage'),
  'referral-staff': () => import('./ReferralStaffPage'),
  'referral-staff-activity': () => import('./ReferralStaffActivityPage'),
  'staff-earnings': () => import('./StaffEarningsPage'),
  'agent-earn': () => import('./AgentEarnPage'),
  'phone-verifications': () => import('./PhoneVerificationsPage'),
  'live-activity': () => import('./LiveActivityPage'),
  segmentation: () => import('./SegmentationPage'),
  insights: () => import('./InsightsDashboardPage'),
  'monthly-analytics': () => import('./MonthlyAnalyticsPage'),
  'rewards-granted': () => import('./RewardsGrantedPage'),
  'events-rewards': () => import('./EventsAndRewardPage'),
  'score-predictions': () => import('./ScorePredictionsPage'),
  ledgers: () => import('./LedgersPage'),
  kyc: () => import('./KycReviewsPage'),
  'tenant-catalog': () => import('./TenantCatalogPage'),
  'promo-codes': () => import('./PromoCodesPage'),
  'installed-devices': () => import('./InstalledDevicesPage'),
  'tenant-activity': () => import('./TenantActivityPage'),
  'provider-balance': () => import('./ProviderBalancePage'),
  'tenant-broadcast': () => import('./TenantBroadcastPage'),
  'approval-bots': () => import('./ApprovalBotsPage'),
  'finance-staff': () => import('./FinanceStaffPage'),
  'survey-staff': () => import('./SurveyStaffPage'),
  'report-emails': () => import('./ReportEmailsPage'),
  experiments: () => import('./ExperimentsPage'),
  'fp-rules': () => import('./FpRulesPage'),
  'ip-security': () => import('./IpSecurityPage'),
  backup: () => import('./BackupPage'),
  'payment-analytics': () => import('./PaymentAnalyticsPage'),
  'game-analytics': () => import('./GameAnalyticsPage'),
  'lre-rules': () => import('./LREEarningRulesPage'),
  'lre-payouts': () => import('./LREPayoutHistoryPage'),
  'sentry-health': () => import('./SentryHealthPage'),
  'promo-banners': () => import('./PromoBannersAdminPage'),
  'tenant-theme': () => import('./TenantThemeEditorPage'),
  'platform-rules': () => import('./PlatformRulesPage'),
  'cashout-rules': () => import('./PlatformRulesPage'),
  // --- Added during UI port (iterations 76-79) - keep in sync with App.js routes ---
  'roulette-segments': () => import('./RouletteSegmentsPage'),
  'platform-branding': () => import('./BrandingPage'),
  'referral-offers': () => import('./ReferralOffersPage'),
  'marketplace-items': () => import('./MarketplaceItemsPage'),
  'daily-weekly-tasks': () => import('./AdminTasksPage'),
  'change-password': () => import('./ChangePasswordPage'),
};

const DashboardPage = lazy(ADMIN_PAGE_LOADERS.dashboard);
const PlayersPage = lazy(ADMIN_PAGE_LOADERS.players);
const TransactionsPage = lazy(ADMIN_PAGE_LOADERS.transactions);
const GameOperationsPage = lazy(ADMIN_PAGE_LOADERS['game-operations']);
const GamesPage = lazy(ADMIN_PAGE_LOADERS.games);
const ProvidersPage = lazy(ADMIN_PAGE_LOADERS.providers);
const PaymentMethodsPage = lazy(ADMIN_PAGE_LOADERS.payments);
const TenantsPage = lazy(ADMIN_PAGE_LOADERS.tenants);
const StaffPage = lazy(ADMIN_PAGE_LOADERS.staff);
const ChatSupportPage = lazy(ADMIN_PAGE_LOADERS.chat);
const GameRecordsPage = lazy(ADMIN_PAGE_LOADERS['game-records']);
const SettingsPage = lazy(ADMIN_PAGE_LOADERS.settings);
const BannersPage = lazy(ADMIN_PAGE_LOADERS.banners);
const TenantAnalyticsPage = lazy(ADMIN_PAGE_LOADERS['tenant-analytics']);
const TasksRewardsPage = lazy(ADMIN_PAGE_LOADERS['tasks-rewards']);
const TelegramConfigPage = lazy(ADMIN_PAGE_LOADERS.telegram);
const NotificationsPage = lazy(ADMIN_PAGE_LOADERS.notifications);
const DepositRequestsPage = lazy(ADMIN_PAGE_LOADERS['deposit-requests']);
const WithdrawalRequestsPage = lazy(ADMIN_PAGE_LOADERS['withdrawal-requests']);
const WithdrawalPortalPage = lazy(ADMIN_PAGE_LOADERS['withdrawal-portal']);
const TiersPage = lazy(ADMIN_PAGE_LOADERS.tiers);
const ProviderHealthPage = lazy(ADMIN_PAGE_LOADERS['provider-health']);
const TenantDashboardPage = lazy(ADMIN_PAGE_LOADERS['tenant-dashboard']);
const TenantSettingsPage = lazy(ADMIN_PAGE_LOADERS['tenant-settings']);
const TenantBrandingPage = lazy(ADMIN_PAGE_LOADERS.branding);
const ActivityAnalyticsPage = lazy(ADMIN_PAGE_LOADERS.activity);
const UserAnalyticsPage = lazy(ADMIN_PAGE_LOADERS['user-analytics']);
const PlayerAnalyticsPage = lazy(ADMIN_PAGE_LOADERS['player-analytics']);
const OperationsCenterPage = lazy(ADMIN_PAGE_LOADERS['operations-center']);
const StaffActivityPage = lazy(ADMIN_PAGE_LOADERS['staff-activity']);
const ReferralStaffPage = lazy(ADMIN_PAGE_LOADERS['referral-staff']);
const ReferralStaffActivityPage = lazy(ADMIN_PAGE_LOADERS['referral-staff-activity']);
const StaffEarningsPage = lazy(ADMIN_PAGE_LOADERS['staff-earnings']);
const AgentEarnPage = lazy(ADMIN_PAGE_LOADERS['agent-earn']);
const PhoneVerificationsPage = lazy(ADMIN_PAGE_LOADERS['phone-verifications']);
const LiveActivityPage = lazy(ADMIN_PAGE_LOADERS['live-activity']);
const SegmentationPage = lazy(ADMIN_PAGE_LOADERS.segmentation);
const InsightsDashboardPage = lazy(ADMIN_PAGE_LOADERS.insights);
const MasterGrowthInsightsPage = lazy(() => import('./MasterGrowthInsightsPage'));
const MonthlyAnalyticsPage = lazy(ADMIN_PAGE_LOADERS['monthly-analytics']);
const RewardsGrantedPage = lazy(ADMIN_PAGE_LOADERS['rewards-granted']);
const EventsAndRewardPage = lazy(ADMIN_PAGE_LOADERS['events-rewards']);
const ScorePredictionsPage = lazy(ADMIN_PAGE_LOADERS['score-predictions']);
const LedgersPage = lazy(ADMIN_PAGE_LOADERS.ledgers);
const KycReviewsPage = lazy(ADMIN_PAGE_LOADERS.kyc);
const TenantCatalogPage = lazy(ADMIN_PAGE_LOADERS['tenant-catalog']);
const PromoCodesPage = lazy(ADMIN_PAGE_LOADERS['promo-codes']);
const InstalledDevicesPage = lazy(ADMIN_PAGE_LOADERS['installed-devices']);
const TenantActivityPage = lazy(ADMIN_PAGE_LOADERS['tenant-activity']);
const ProviderBalancePage = lazy(ADMIN_PAGE_LOADERS['provider-balance']);
const TenantBroadcastPage = lazy(ADMIN_PAGE_LOADERS['tenant-broadcast']);
const ApprovalBotsPage = lazy(ADMIN_PAGE_LOADERS['approval-bots']);
const FinanceStaffPage = lazy(ADMIN_PAGE_LOADERS['finance-staff']);
const SurveyStaffPage = lazy(ADMIN_PAGE_LOADERS['survey-staff']);
const ReportEmailsPage = lazy(ADMIN_PAGE_LOADERS['report-emails']);
const ExperimentsPage = lazy(ADMIN_PAGE_LOADERS.experiments);
const FpRulesPage = lazy(ADMIN_PAGE_LOADERS['fp-rules']);
const IpSecurityPage = lazy(ADMIN_PAGE_LOADERS['ip-security']);
const BackupPage = lazy(ADMIN_PAGE_LOADERS.backup);
const PaymentAnalyticsPage = lazy(ADMIN_PAGE_LOADERS['payment-analytics']);
const GameAnalyticsPage = lazy(ADMIN_PAGE_LOADERS['game-analytics']);
const LREEarningRulesPage = lazy(ADMIN_PAGE_LOADERS['lre-rules']);
const LREPayoutHistoryPage = lazy(ADMIN_PAGE_LOADERS['lre-payouts']);
const SentryHealthPage = lazy(ADMIN_PAGE_LOADERS['sentry-health']);
const PromoBannersAdminPage = lazy(ADMIN_PAGE_LOADERS['promo-banners']);
const TenantThemeEditorPage = lazy(ADMIN_PAGE_LOADERS['tenant-theme']);
const PlatformRulesPage = lazy(ADMIN_PAGE_LOADERS['platform-rules']);
const RouletteSegmentsPage = lazy(ADMIN_PAGE_LOADERS['roulette-segments']);
const BrandingPage = lazy(ADMIN_PAGE_LOADERS['platform-branding']);
const ReferralOffersPage = lazy(ADMIN_PAGE_LOADERS['referral-offers']);
const MarketplaceItemsPage = lazy(ADMIN_PAGE_LOADERS['marketplace-items']);
const AdminTasksPage = lazy(ADMIN_PAGE_LOADERS['daily-weekly-tasks']);
const ChangePasswordPage = lazy(ADMIN_PAGE_LOADERS['change-password']);

const prefetchAdminPage = (tabId) => {
  const loader = ADMIN_PAGE_LOADERS[tabId];
  if (loader) loader().catch(() => {});
};

function AdminPageFallback({ compact = false }) {
  return (
    <div className={`flex flex-col items-center justify-center gap-3 rounded-2xl border border-white/10 bg-[#101722]/70 text-gray-400 ${compact ? 'h-full min-h-[360px]' : 'min-h-[calc(100vh-8rem)]'}`}>
      <div className="spinner" />
      <p className="text-sm font-semibold">Loading section...</p>
    </div>
  );
}

const ADMIN_MENU = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['admin', 'moderator', 'support'] },
  { id: 'insights', label: 'Growth Insights', icon: BarChart3, roles: ['admin', 'moderator'], perm: 'analytics:view' },
  { id: 'monthly-analytics', label: 'Monthly Analytics', icon: CalendarDays, roles: ['admin', 'moderator', 'finance'], perm: 'analytics:view' },
  { id: 'rewards-granted', label: 'Rewards Granted', icon: Gift, roles: ['admin', 'moderator', 'finance'], perm: 'analytics:view' },
  { id: 'events-rewards', label: 'Events and Reward', icon: Trophy, roles: ['admin', 'moderator'], perm: 'promotions:manage' },
  { id: 'score-predictions', label: 'Predict the Score', icon: Trophy, roles: ['admin', 'moderator'], perm: 'promotions:manage' },
  { id: 'payment-analytics', label: 'Payment Analytics', icon: CreditCard, roles: ['admin', 'finance'], perm: 'reports:read' },
  { id: 'ledgers', label: 'Ledgers', icon: ScrollText, roles: ['admin', 'moderator', 'finance'], perm: 'analytics:view' },
  { id: 'tenant-activity', label: 'Site Activity', icon: Building2, masterOnly: true, roles: ['admin'], perm: 'analytics:view' },
  { id: 'tenant-analytics', label: 'Site Analytics', icon: BarChart3, masterOnly: true, roles: ['admin'], perm: 'analytics:view' },
  { id: 'players', label: 'Players', icon: Users, roles: ['admin', 'moderator', 'support'], perm: 'users:view' },
  { id: 'player-analytics', label: 'Player Analytics', icon: Sparkles, roles: ['admin', 'moderator', 'finance'], perm: 'analytics:view' },
  { id: 'user-analytics', label: 'User Analytics', icon: Users, roles: ['admin', 'moderator'], perm: 'analytics:view' },
  { id: 'transactions', label: 'Transactions', icon: DollarSign, roles: ['admin', 'moderator'], perm: 'transactions:view' },
  { id: 'game-operations', label: 'Pending Game Operations', icon: ShieldCheck, roles: ['admin', 'moderator'], perm: 'transactions:view' },
  { id: 'deposit-requests', label: 'Deposit Requests', icon: DollarSign, roles: ['admin', 'moderator'], perm: 'payments:view' },
  { id: 'withdrawal-requests', label: 'Withdrawal Requests', icon: DollarSign, roles: ['admin', 'moderator'], perm: 'payments:view' },
  { id: 'withdrawal-portal', label: 'Withdrawal Portal', icon: Wallet, masterOnly: true, roles: ['admin'], perm: 'payments:view' },
  { id: 'kyc', label: 'KYC Reviews', icon: Shield, roles: ['admin', 'moderator'], perm: 'users:manage' },
  { id: 'game-records', label: 'Game Records', icon: BarChart3, roles: ['admin'], perm: 'analytics:view' },
  { id: 'activity', label: 'Activity Analytics', icon: Activity, roles: ['admin'], perm: 'analytics:view' },
  { id: 'chat', label: 'Live Chat', icon: MessageCircle, roles: ['admin', 'moderator', 'support'], perm: 'chat:respond' },
  { id: 'game-analytics', label: 'Game Analytics', icon: Gamepad2, roles: ['admin', 'finance'], perm: 'reports:read' },
  { id: 'provider-balance', label: 'Provider Balance', icon: Wallet, roles: ['admin'], perm: 'analytics:view' },
  { id: 'payments', label: 'Payments API', icon: CreditCard, roles: ['admin'], perm: 'system:config' },
  { id: 'platform-rules', label: 'Platform Rules', icon: ScrollText, roles: ['admin'], perm: 'system:config' },
  { id: 'approval-bots', label: 'Payment Approval Bots', icon: Bot, roles: ['admin'] },
  { id: 'games', label: 'Games', icon: Gamepad2, roles: ['admin'], perm: 'system:config' },
  { id: 'tenant-catalog', label: 'Game Visibility', icon: Layers, roles: ['admin'], perm: 'system:config' },
  { id: 'providers', label: 'Game Providers', icon: Server, roles: ['admin'], perm: 'system:config' },
  { id: 'tenant-broadcast', label: 'Broadcast', icon: Megaphone, masterOnly: true, roles: ['admin'] },
  { id: 'tasks-rewards', label: 'Tasks & Rewards', icon: Trophy, roles: ['admin'], perm: 'promotions:edit' },
  // --- Ported UI additions (iterations 76-79) ---
  { id: 'daily-weekly-tasks', label: 'Daily / Weekly Tasks', icon: CalendarDays, roles: ['admin'], perm: 'promotions:edit' },
  { id: 'marketplace-items', label: 'Marketplace (FP→Cash)', icon: ShoppingBag, roles: ['admin'], perm: 'promotions:edit' },
  { id: 'roulette-segments', label: 'Roulette Wheel', icon: Disc3, roles: ['admin'], perm: 'promotions:edit' },
  { id: 'referral-offers', label: 'Referral Tier Offers', icon: Tag, roles: ['admin'], perm: 'promotions:edit' },
  { id: 'platform-branding', label: 'Platform Branding', icon: Sparkles, masterOnly: true, roles: ['admin'] },
  { id: 'promo-codes', label: 'Promo Codes', icon: Tag, roles: ['admin'], perm: 'promotions:manage' },
  { id: 'installed-devices', label: 'Installed Devices', icon: Download, roles: ['admin'], perm: 'analytics:view' },
  { id: 'report-emails', label: 'Report Emails', icon: Mail, roles: ['admin'], perm: 'settings:edit' },
  { id: 'experiments', label: 'A/B Experiments', icon: FlaskConical, roles: ['admin'], perm: 'promotions:manage' },
  { id: 'tiers', label: 'VIP Tiers & XP', icon: Star, roles: ['admin'], perm: 'system:config' },
  { id: 'fp-rules', label: 'FP Rules', icon: Sparkles, roles: ['admin'], perm: 'system:config' },
  { id: 'ip-security', label: 'IP Security', icon: Shield, roles: ['admin'], perm: 'system:config' },
  { id: 'backup', label: 'Atlas Backup', icon: Cloud, masterOnly: true, roles: ['admin'], perm: 'system:config' },
  { id: 'banners', label: 'Text Announcements', icon: FileText, roles: ['admin', 'moderator'], perm: 'announcements:manage' },
  { id: 'promo-banners', label: 'Promo Artwork', icon: Megaphone, roles: ['admin', 'moderator'], perm: 'announcements:manage' },
  { id: 'tenants', label: 'Tenants', icon: Building2, masterOnly: true, roles: ['admin'] },
  { id: 'staff', label: 'Staff', icon: Shield, roles: ['admin'], perm: 'staff:manage' },
  { id: 'referral-staff', label: 'Referral Staff', icon: Users, roles: ['admin'], perm: 'staff:manage' },
  { id: 'agent-earn', label: 'Agent Earn Scheme', icon: DollarSign, roles: ['admin'], perm: 'staff:manage' },
  { id: 'referral-staff-activity', label: 'Referral Staff Activity', icon: Activity, roles: ['admin'], perm: 'staff:manage' },
  { id: 'staff-earnings', label: 'Staff Earnings', icon: DollarSign, roles: ['admin'], perm: 'staff:manage' },
  { id: 'lre-rules', label: 'Referral Earning Rules', icon: DollarSign, roles: ['admin'], perm: 'staff:manage' },
  { id: 'lre-payouts', label: 'LRE Payout History', icon: DollarSign, roles: ['admin'], perm: 'staff:manage' },
  { id: 'phone-verifications', label: 'Manual Phone Verify', icon: Phone, masterOnly: true, roles: ['admin'], perm: 'users:manage' },
  { id: 'notifications', label: 'Notifications', icon: Bell, roles: ['admin', 'moderator'], perm: 'notifications:broadcast' },
  { id: 'telegram', label: 'Telegram Bots', icon: Bot, roles: ['admin'], perm: 'system:config' },
  { id: 'finance-staff', label: 'Finance Staff', icon: Shield, masterOnly: true, roles: ['admin'] },
  { id: 'survey-staff', label: 'Survey', icon: ClipboardList, masterOnly: true, roles: ['admin'] },
  { id: 'staff-activity', label: 'Staff Activity', icon: Shield, roles: ['admin'], perm: 'audit:view' },
  { id: 'live-activity', label: 'Live Activity', icon: Zap, roles: ['admin'], perm: 'analytics:view' },
  { id: 'segmentation', label: 'Segmentation', icon: PieChart, roles: ['admin'], perm: 'analytics:view' },
  { id: 'provider-health', label: 'Provider Health', icon: Activity, masterOnly: true, roles: ['admin'] },
  { id: 'sentry-health', label: 'Observability — Sentry', icon: Bug, masterOnly: true, roles: ['admin'] },
  { id: 'settings', label: 'Settings', icon: Settings, roles: ['admin'], perm: 'system:config' },
  { id: 'change-password', label: 'Change Password', icon: Lock, tenantOnly: true, roles: ['admin'] },
  { id: 'branding', label: 'Branding & Customization', icon: Palette, tenantOnly: true, roles: ['admin'] },
];

const TENANT_HIDDEN_MENU = new Set([
  'user-analytics',
  'activity',
  'live-activity',
  'segmentation',
  'tasks-rewards',
  'daily-weekly-tasks',
  'referral-offers',
  'promo-codes',
  'installed-devices',
  'report-emails',
  'experiments',
  'fp-rules',
  'referral-staff',
  'agent-earn',
  'referral-staff-activity',
  'staff-earnings',
  'lre-rules',
  'lre-payouts',
]);

const TENANT_MENU_ORDER = [
  'dashboard',
  'insights',
  'monthly-analytics',
  'rewards-granted',
  'events-rewards',
  'score-predictions',
  'ledgers',
  'players',
  'player-analytics',
  'transactions',
  'game-operations',
  'deposit-requests',
  'withdrawal-requests',
  'kyc',
  'payment-analytics',
  'provider-balance',
  'game-records',
  'game-analytics',
  'roulette-segments',
  'marketplace-items',
  'games',
  'tenant-catalog',
  'providers',
  'payments',
  'platform-rules',
  'ip-security',
  'approval-bots',
  'telegram',
  'chat',
  'notifications',
  'staff',
  'staff-activity',
  'phone-verifications',
  'banners',
  'promo-banners',
  'tiers',
  'branding',
  'settings',
  'change-password',
];

const TENANT_LABEL_OVERRIDES = {
  insights: 'Growth Insights',
  'monthly-analytics': 'Monthly Analytics',
  'rewards-granted': 'Rewards Granted',
  'events-rewards': 'Events and Reward',
  'score-predictions': 'Predict the Score',
  'tenant-catalog': 'Game Visibility',
  'roulette-segments': 'Roulette Wheel',
  'marketplace-items': 'Marketplace',
  payments: 'Payment Methods',
  'approval-bots': 'Approval Bots',
  'ip-security': 'IP Security',
  staff: 'Staff Access',
  'staff-activity': 'Staff Audit',
  banners: 'Text Announcements',
  'promo-banners': 'Promo Artwork',
  'change-password': 'Change Password',
};

const MENU_GROUPS = {
  dashboard: 'Overview',
  insights: 'Overview',
  'monthly-analytics': 'Overview',
  'rewards-granted': 'Overview',
  'events-rewards': 'Overview',
  'score-predictions': 'Overview',
  ledgers: 'Overview',
  players: 'Players',
  'player-analytics': 'Players',
  transactions: 'Finance',
  'deposit-requests': 'Finance',
  'withdrawal-requests': 'Finance',
  'withdrawal-portal': 'Finance',
  kyc: 'Finance',
  'payment-analytics': 'Finance',
  'provider-balance': 'Finance',
  'game-records': 'Games',
  'game-analytics': 'Games',
  'roulette-segments': 'Games',
  'marketplace-items': 'Games',
  games: 'Games',
  'tenant-catalog': 'Games',
  providers: 'Games',
  payments: 'Setup',
  'platform-rules': 'Setup',
  'ip-security': 'Setup',
  'approval-bots': 'Support & Bots',
  telegram: 'Support & Bots',
  chat: 'Support & Bots',
  notifications: 'Support & Bots',
  staff: 'Staff',
  'staff-activity': 'Staff',
  'phone-verifications': 'Players',
  banners: 'Brand & Growth',
  'promo-banners': 'Brand & Growth',
  tiers: 'Brand & Growth',
  branding: 'Brand & Growth',
  settings: 'Settings',
  'change-password': 'Settings',
  'survey-staff': 'Staff',
};

const tenantMenuLabel = (item) => TENANT_LABEL_OVERRIDES[item.id] || item.label;
const SURVEY_MENU_IDS = new Set(['players', 'player-analytics', 'chat', 'withdrawal-portal']);
const SINGLE_SITE_HIDDEN_MENU = new Set([
  'tenants',
  'tenant-activity',
  'tenant-analytics',
  'tenant-broadcast',
]);

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { tenantSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const tenantAdminBgFallback = 'linear-gradient(90deg, #152235 0%, #17283A 34%, #1D3349 67%, #223B54 100%)';
  const tenantAdminSurfaceFallback = 'rgba(21, 34, 53, 0.96)';
  const tenantAdminInputFallback = 'rgba(12, 24, 39, 0.68)';
  
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [navSearch, setNavSearch] = useState('');
  const [tenantInfo, setTenantInfo] = useState(null);
  const [myPerms, setMyPerms] = useState(null); // null = loading; array = loaded

  const fetchMyPermissions = useCallback(async () => {
    try {
      const res = await api.get('/api/admin/staff/my-permissions');
      const data = res.data || {};
      setMyPerms({
        list: data.permissions || [],
        master: Boolean(data.is_master_admin),
        role: data.role || user?.role,
      });
    } catch {
      setMyPerms({ list: [], master: false, role: user?.role });
    }
  }, [user?.role]);

  useEffect(() => {
    fetchMyPermissions();

    const refreshWhenVisible = () => {
      if (!document.hidden) fetchMyPermissions();
    };
    const refreshOnFocus = () => fetchMyPermissions();
    const interval = window.setInterval(fetchMyPermissions, 30000);

    document.addEventListener('visibilitychange', refreshWhenVisible);
    window.addEventListener('focus', refreshOnFocus);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
      window.removeEventListener('focus', refreshOnFocus);
    };
  }, [fetchMyPermissions]);

  useEffect(() => {
    const openWorkspaceMenu = () => setMobileSidebarOpen(true);
    window.addEventListener(OPEN_ADMIN_WORKSPACE_MENU_EVENT, openWorkspaceMenu);
    return () => window.removeEventListener(OPEN_ADMIN_WORKSPACE_MENU_EVENT, openWorkspaceMenu);
  }, []);

  // Fetch tenant branding for tenant admins
  useEffect(() => {
    if (user?.tenant_id && !user?.is_master_admin) {
      // Get tenant via slug (tenantSlug from URL) or by id
      if (tenantSlug) {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tenants/public/${tenantSlug}`)
          .then(r => r.ok ? r.json() : null)
          .then(d => d && setTenantInfo(d))
          .catch(() => {});
      }
    }
  }, [user?.tenant_id, user?.is_master_admin, tenantSlug]);

  useEffect(() => {
    // Parse active tab from URL
    const path = location.pathname;
    const segments = path.split('/').filter(Boolean);
    const lastSegment = segments[segments.length - 1];
    
    if (lastSegment && lastSegment !== 'admin' && ADMIN_MENU.some(m => m.id === lastSegment)) {
      setActiveTab(lastSegment);
    }
  }, [location]);

  const handleLogout = async () => {
    await logout();
    window.location.href = location.pathname.startsWith('/survey')
      ? '/survey/auth'
      : (tenantSlug ? `/${tenantSlug}/admin/auth` : '/admin/auth');
  };

  const handleTabChange = (tabId) => {
    prefetchAdminPage(tabId);
    setActiveTab(tabId);
    setMobileSidebarOpen(false);
    setNavSearch('');
    const basePath = location.pathname.startsWith('/survey') ? '/survey' : (tenantSlug ? `/${tenantSlug}/admin` : '/admin');
    navigate(tabId === 'dashboard' ? basePath : `${basePath}/${tabId}`);
  };

  const isTenantAdmin = !user?.is_master_admin && !!user?.tenant_id;
  const isMasterAdmin = Boolean(user?.is_master_admin || myPerms?.master);
  const effectiveRole = myPerms?.role || user?.role;
  const isSurveyStaff = effectiveRole === 'survey_staff';
  const isRoleAdmin = effectiveRole === 'admin';
  const tenantBranding = tenantInfo?.branding || {};
  const tenantTheme = tenantBranding.theme || {};
  const tenantDisplayName = (
    tenantTheme.brand_name ||
    tenantBranding.brand_name ||
    tenantInfo?.name ||
    user?.tenant_name ||
    tenantSlug ||
    'Tenant'
  );
  const tenantAccent = tenantTheme.btn_primary_from || tenantTheme.neon || tenantBranding.primary_color || '#D4AF37';
  const tenantAccent2 = tenantTheme.btn_primary_to || tenantBranding.secondary_color || '#F5D76E';
  const tenantLogoUrl = tenantTheme.logo_url || tenantTheme.intro_logo_url || tenantBranding.logo_url || '';
  const tenantAdminBg = tenantTheme.admin_bg || tenantTheme.bg || tenantAdminBgFallback;
  const tenantAdminSurface = tenantTheme.admin_surface || tenantTheme.surface || tenantAdminSurfaceFallback;
  const tenantAdminInput = tenantTheme.admin_input || tenantAdminInputFallback;
  const adminChromeStyle = isTenantAdmin ? { background: tenantAdminSurface } : undefined;
  const adminInputStyle = isTenantAdmin ? { background: tenantAdminInput } : undefined;

  const filteredMenu = useMemo(() => ADMIN_MENU.filter(item => {
    const hasStaffRole = hasAdminWorkspaceRole(effectiveRole);
    if (!hasStaffRole) return false;
    if (SINGLE_SITE_HIDDEN_MENU.has(item.id) && !isMasterAdmin) return false;
    if (isSurveyStaff && !SURVEY_MENU_IDS.has(item.id)) return false;
    if (item.masterOnly && !(user?.is_master_admin || myPerms?.master)) return false;
    if (item.tenantOnly && !isTenantAdmin) return false;
    if (isTenantAdmin && TENANT_HIDDEN_MENU.has(item.id)) return false;
    if (item.id === 'dashboard' && !isRoleAdmin && myPerms && !myPerms.list.some(p => p !== 'chat:respond')) return false;
    if (isRoleAdmin) {
      return item.roles.includes('admin');
    }
    // Explicit permissions are the source of truth for staff/moderator/support.
    if (item.perm) {
      if (!myPerms) return true; // show while loading to avoid flicker
      return myPerms.list.includes(item.perm);
    }
    if (item.id === 'dashboard') return true;
    return item.roles.includes(effectiveRole);
  }).sort((a, b) => {
    if (!isTenantAdmin) return 0;
    const ai = TENANT_MENU_ORDER.indexOf(a.id);
    const bi = TENANT_MENU_ORDER.indexOf(b.id);
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
  }), [effectiveRole, user?.is_master_admin, isMasterAdmin, isTenantAdmin, isRoleAdmin, isSurveyStaff, myPerms]);

  // White Room is a staff workspace, not a chat-only shell. Keep the existing
  // chat component alive for staff who have that permission so its websocket
  // and device/browser notifications remain active while they work elsewhere.
  const hasLiveChatAccess = hasLiveChatMenu(filteredMenu);

  useEffect(() => {
    if (!filteredMenu.length) return undefined;
    const allowedIds = new Set(filteredMenu.map(item => item.id));
    const priorityIds = [
      activeTab,
      'dashboard',
      'insights',
      'rewards-granted',
      'events-rewards',
      'score-predictions',
      'ledgers',
      'players',
      'transactions',
      'game-operations',
      'deposit-requests',
      'withdrawal-requests',
      'kyc',
      'withdrawal-portal',
      'payments',
      'platform-rules',
      'provider-balance',
      'game-records',
      'game-analytics',
      'games',
      'chat',
      'staff',
      'settings',
      'change-password',
      'branding',
      'tenants',
    ];
    const warmSections = () => {
      priorityIds.forEach((id) => {
        if (allowedIds.has(id) || id === activeTab) prefetchAdminPage(id);
      });
    };

    if ('requestIdleCallback' in window) {
      const idleId = window.requestIdleCallback(warmSections, { timeout: 2500 });
      return () => window.cancelIdleCallback?.(idleId);
    }
    const timer = window.setTimeout(warmSections, 1000);
    return () => window.clearTimeout(timer);
  }, [activeTab, filteredMenu]);

  const navigateToAllowedTab = useCallback((tabId) => {
    setActiveTab(tabId);
    setMobileSidebarOpen(false);
    setNavSearch('');
    const basePath = location.pathname.startsWith('/survey') ? '/survey' : (tenantSlug ? `/${tenantSlug}/admin` : '/admin');
    navigate(tabId === 'dashboard' ? basePath : `${basePath}/${tabId}`, { replace: true });
  }, [location.pathname, navigate, tenantSlug]);

  useEffect(() => {
    if (!myPerms || filteredMenu.length === 0) return;
    if (!filteredMenu.some(item => item.id === activeTab)) {
      navigateToAllowedTab(filteredMenu[0].id);
    }
  }, [myPerms, filteredMenu, activeTab, navigateToAllowedTab]);

  const normalizeSearch = (value) => value.trim().toLowerCase();
  const navQuery = normalizeSearch(navSearch);
  const searchedMenu = navQuery
    ? filteredMenu.filter(item => {
        const label = isTenantAdmin ? tenantMenuLabel(item) : item.label;
        const haystack = `${label} ${item.label} ${item.id}`.replace(/-/g, ' ').toLowerCase();
        return haystack.includes(navQuery);
      })
    : filteredMenu;
  const canQuickOpen = navQuery && searchedMenu.length > 0;

  const handleNavSearchKeyDown = (e) => {
    if (e.key === 'Enter' && canQuickOpen) {
      e.preventDefault();
      handleTabChange(searchedMenu[0].id);
    }
    if (e.key === 'Escape') {
      setNavSearch('');
    }
  };

  const renderPage = () => {
    if (SINGLE_SITE_HIDDEN_MENU.has(activeTab) && !isMasterAdmin) {
      return <DashboardPage />;
    }
    // Special-case the theme editor — its URL is /admin/tenant-theme/<tenantId>
    // and the last segment is the tenant id, not a known menu key.
    const path = location.pathname;
    const themeMatch = path.match(/\/admin\/tenant-theme\/([\w-]+)/i);
    if (themeMatch) {
      return <TenantThemeEditorPage />;
    }
    switch (activeTab) {
      case 'dashboard': return isTenantAdmin ? <TenantDashboardPage onNavigate={handleTabChange} /> : <DashboardPage />;
      case 'insights': return user?.is_master_admin ? <MasterGrowthInsightsPage /> : <InsightsDashboardPage />;
      case 'monthly-analytics': return <MonthlyAnalyticsPage />;
      case 'rewards-granted': return <RewardsGrantedPage />;
      case 'events-rewards': return <EventsAndRewardPage />;
      case 'score-predictions': return <ScorePredictionsPage />;
      case 'ledgers': return <LedgersPage />;
      case 'operations-center': return <OperationsCenterPage />;
      case 'tenant-activity': return <TenantActivityPage />;
      case 'players': return <PlayersPage />;
      case 'player-analytics': return <PlayerAnalyticsPage />;
      case 'user-analytics': return <UserAnalyticsPage />;
      case 'transactions': return <TransactionsPage />;
      case 'game-operations': return <GameOperationsPage />;
      case 'games': return <GamesPage />;
      case 'tenant-catalog': return <TenantCatalogPage />;
      case 'providers': return <ProvidersPage />;
      case 'provider-balance': return <ProviderBalancePage />;
      case 'tenant-broadcast': return <TenantBroadcastPage />;
      case 'payments': return <PaymentMethodsPage />;
      case 'platform-rules': return <PlatformRulesPage onNavigate={handleTabChange} />;
      case 'tenants': return <TenantsPage />;
      case 'tenant-analytics': return <TenantAnalyticsPage />;
      case 'staff': return <StaffPage />;
      case 'chat': return <ChatSupportPage />;
      case 'notifications': return <NotificationsPage />;
      case 'game-records': return <GameRecordsPage />;
      case 'settings': return isTenantAdmin ? <TenantSettingsPage /> : <SettingsPage />;
      case 'change-password': return <ChangePasswordPage />;
      case 'branding': return <TenantBrandingPage />;
      case 'banners': return <BannersPage />;
      case 'tasks-rewards': return <TasksRewardsPage />;
      case 'daily-weekly-tasks': return <AdminTasksPage />;
      case 'marketplace-items': return <MarketplaceItemsPage />;
      case 'roulette-segments': return <RouletteSegmentsPage />;
      case 'referral-offers': return <ReferralOffersPage />;
      case 'platform-branding': return <BrandingPage />;
      case 'telegram': return <TelegramConfigPage />;
      case 'approval-bots': return <ApprovalBotsPage />;
      case 'finance-staff': return <FinanceStaffPage />;
      case 'survey-staff': return <SurveyStaffPage />;
      case 'deposit-requests': return <DepositRequestsPage />;
      case 'withdrawal-requests': return <WithdrawalRequestsPage />;
      case 'withdrawal-portal': return <WithdrawalPortalPage />;
      case 'kyc': return <KycReviewsPage />;
      case 'tiers': return <TiersPage />;
      case 'fp-rules': return <FpRulesPage />;
      case 'ip-security': return <IpSecurityPage />;
      case 'backup': return <BackupPage />;
      case 'payment-analytics': return <PaymentAnalyticsPage />;
      case 'game-analytics': return <GameAnalyticsPage />;
      case 'provider-health': return <ProviderHealthPage />;
      case 'sentry-health': return <SentryHealthPage />;
      case 'cashout-rules': return <PlatformRulesPage onNavigate={handleTabChange} />;
      case 'promo-banners': return <PromoBannersAdminPage />;
      case 'activity': return <ActivityAnalyticsPage />;
      case 'staff-activity': return <StaffActivityPage />;
      case 'referral-staff': return <ReferralStaffPage />;
      case 'agent-earn': return <AgentEarnPage />;
      case 'referral-staff-activity': return <ReferralStaffActivityPage />;
      case 'staff-earnings': return <StaffEarningsPage />;
      case 'lre-rules': return <LREEarningRulesPage />;
      case 'lre-payouts': return <LREPayoutHistoryPage />;
      case 'phone-verifications': return <PhoneVerificationsPage />;
      case 'promo-codes': return <PromoCodesPage />;
      case 'installed-devices': return <InstalledDevicesPage />;
      case 'report-emails': return <ReportEmailsPage />;
      case 'experiments': return <ExperimentsPage />;
      case 'live-activity': return <LiveActivityPage />;
      case 'segmentation': return <SegmentationPage />;
      default: return isTenantAdmin ? <TenantDashboardPage onNavigate={handleTabChange} /> : <DashboardPage />;
    }
  };

  return (
    <div className={`admin-shell ${isTenantAdmin ? 'admin-shell-tenant' : 'admin-shell-master'} h-screen flex overflow-hidden`} style={{ background: isTenantAdmin ? tenantAdminBg : '#0B0E14' }} data-testid="admin-layout">
      {/* Sidebar */}
      <aside
        className={`admin-sidebar fixed lg:sticky lg:top-0 inset-y-0 left-0 z-[100] w-[86vw] max-w-[19rem] sm:w-72 lg:w-64 h-screen shrink-0 bg-[#131822] border-r border-white/5 transition-transform duration-200 flex flex-col ${
          mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
        style={adminChromeStyle}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-white/5 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            {isTenantAdmin ? (
              <>
                {tenantLogoUrl ? (
                  <img src={tenantLogoUrl.startsWith('http') ? tenantLogoUrl : `${process.env.REACT_APP_BACKEND_URL}${tenantLogoUrl}`}
                    alt={tenantDisplayName} className="w-9 h-9 rounded-lg object-cover flex-shrink-0" data-testid="admin-tenant-logo" />
                ) : (
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `linear-gradient(135deg, ${tenantAccent}, ${tenantAccent2})` }}>
                    <span className="text-sm font-bold text-black">{(tenantDisplayName || 'T').charAt(0).toUpperCase()}</span>
                  </div>
                )}
                <div className="min-w-0">
                  <span className="font-bold truncate block text-sm" style={{ color: tenantAccent }} data-testid="admin-tenant-name">{tenantDisplayName}</span>
                  <p className="text-[10px] text-gray-400 capitalize">{user?.role?.replace('_', ' ')} · Tenant Admin</p>
                </div>
              </>
            ) : (
              <>
                <div className="min-w-0">
                  <span className="font-bold gold-text">Master Admin</span>
                  <p className="text-xs text-gray-400">Global Control Plane</p>
                </div>
              </>
            )}
          </div>
          <button onClick={() => setMobileSidebarOpen(false)} className="lg:hidden text-gray-400 hover:text-white">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <div className="px-4 pt-3 flex-shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              value={navSearch}
              onChange={(e) => setNavSearch(e.target.value)}
              onKeyDown={handleNavSearchKeyDown}
              placeholder="Search admin menu..."
              className="pl-9 pr-10 bg-[#0B0E14] border-white/10 h-9 text-sm"
              style={adminInputStyle}
              data-testid="admin-nav-search"
            />
            {navSearch && (
              <button
                type="button"
                onClick={() => setNavSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 rounded text-[11px] text-gray-500 hover:text-white hover:bg-white/5"
                aria-label="Clear admin menu search"
              >
                Clear
              </button>
            )}
          </div>
          {navQuery && (
            <p className="mt-2 text-[11px] text-gray-500" data-testid="admin-nav-search-count">
              {searchedMenu.length ? `${searchedMenu.length} section${searchedMenu.length === 1 ? '' : 's'} found` : 'No matching admin sections'}
            </p>
          )}
        </div>
        <nav className="p-4 space-y-1 flex-1 overflow-y-auto scrollbar-thin">
          {searchedMenu.map((item, index) => {
            const displayLabel = isTenantAdmin ? tenantMenuLabel(item) : item.label;
            const group = isTenantAdmin ? MENU_GROUPS[item.id] || 'More' : null;
            const previousGroup = isTenantAdmin && index > 0 ? (MENU_GROUPS[searchedMenu[index - 1]?.id] || 'More') : null;
            const showGroup = isTenantAdmin && !navQuery && group !== previousGroup;
            return (
              <Fragment key={item.id}>
                {showGroup && (
                  <div className="px-3 pt-3 pb-1 text-[10px] font-black uppercase tracking-[0.18em] text-gray-500 first:pt-0">
                    {group}
                  </div>
                )}
                <button
                  onClick={() => handleTabChange(item.id)}
                  className={`sidebar-item w-full ${activeTab === item.id ? 'active' : ''}`}
                  data-testid={`nav-${item.id}`}
                >
                  <item.icon className="w-5 h-5 shrink-0" />
                  <span className="truncate">{displayLabel}</span>
                </button>
              </Fragment>
            );
          })}
          {navQuery && searchedMenu.length === 0 && (
            <div className="px-3 py-6 text-center text-xs text-gray-500 border border-dashed border-white/10 rounded-xl">
              No menu item matches "{navSearch}".
            </div>
          )}
        </nav>

        {/* User Info */}
        <div className="p-4 border-t border-white/5 flex-shrink-0 space-y-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center text-[#D4AF37] font-bold">
              {user?.full_name?.charAt(0).toUpperCase() || 'A'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.full_name}</p>
              <p className="text-xs text-gray-400 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 hover:text-red-300 text-sm font-semibold transition"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {mobileSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-[90] lg:hidden"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="admin-main flex-1 flex flex-col min-w-0 h-screen">
        {/* Header */}
        <header
          className="admin-header h-16 flex items-center justify-between px-3 sm:px-4 lg:px-6 border-b border-white/5 bg-[#131822] flex-shrink-0"
          style={adminChromeStyle}
        >
          <div className="flex items-center gap-4">
            <button onClick={() => setMobileSidebarOpen(true)} className="lg:hidden p-2 rounded-lg hover:bg-white/5">
              <Menu className="w-5 h-5" />
            </button>
            <h1 className="text-lg sm:text-xl font-semibold capitalize truncate max-w-[58vw] sm:max-w-none">
              {(() => {
                const activeItem = filteredMenu.find(m => m.id === activeTab);
                return activeItem ? (isTenantAdmin ? tenantMenuLabel(activeItem) : activeItem.label) : activeTab.replace(/-/g, ' ');
              })()}
            </h1>
            {isTenantAdmin && tenantInfo && (
              <span className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold"
                style={{ background: `${tenantInfo.branding?.primary_color || '#D4AF37'}1a`, color: tenantInfo.branding?.primary_color || '#D4AF37', border: `1px solid ${tenantInfo.branding?.primary_color || '#D4AF37'}40` }}
                data-testid="admin-tenant-badge">
                {tenantInfo.name}
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            <div className="relative hidden sm:block">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={navSearch}
                onChange={(e) => setNavSearch(e.target.value)}
                onKeyDown={handleNavSearchKeyDown}
                placeholder="Search admin..."
                className="pl-9 w-56 bg-[#0B0E14] border-white/10 h-9"
                style={adminInputStyle}
                data-testid="admin-header-search"
              />
              {canQuickOpen && (
                <div className="absolute right-0 top-11 w-72 rounded-xl border border-white/10 bg-[#131822] shadow-2xl overflow-hidden z-50" data-testid="admin-header-search-results">
                  <div className="px-3 py-2 text-[11px] uppercase tracking-wide text-gray-500 border-b border-white/5">
                    Press Enter to open first result
                  </div>
                  {searchedMenu.slice(0, 6).map(item => (
                    <button
                      key={item.id}
                      onClick={() => handleTabChange(item.id)}
                      className="w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm hover:bg-white/5"
                      data-testid={`admin-search-result-${item.id}`}
                    >
                      <item.icon className="w-4 h-4 text-[#D4AF37]" />
                      <span className="text-gray-100">{isTenantAdmin ? tenantMenuLabel(item) : item.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button className="p-2 rounded-lg hover:bg-white/5 relative">
              <Bell className="w-5 h-5 text-gray-400" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 hover:text-red-300 text-xs font-semibold transition"
              data-testid="admin-header-logout-btn"
              title="Sign out"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </header>

        {/* Page Content */}
        <main
          className={`admin-content flex-1 min-h-0 ${activeTab === 'chat' ? 'overflow-hidden p-0 md:p-3' : 'overflow-y-auto p-3 sm:p-4 lg:p-6'}`}
          style={{ contain: 'none' }}
        >
          <Suspense fallback={<AdminPageFallback compact={activeTab === 'chat'} />}>
            {hasLiveChatAccess && (
              <div className={activeTab === 'chat' ? 'h-full' : 'hidden'} aria-hidden={activeTab !== 'chat'}>
                <ChatSupportPage />
              </div>
            )}
            {activeTab !== 'chat' && renderPage()}
            {activeTab === 'chat' && !hasLiveChatAccess && renderPage()}
          </Suspense>
        </main>
      </div>
    </div>
  );
}
